import { API_CONFIG } from './config'
import { useAuthStore } from '../stores/authStore'

class ApiClient {
    private baseURL: string

    constructor() {
        this.baseURL = API_CONFIG.BASE_URL
    }

    private async makeRequest(url: string, options: RequestInit = {}, timeout: number = 30000): Promise<Response> {
        const { accessToken, refreshToken, logout } = useAuthStore.getState()

        // Token süresi dolmadan önce yenile (5 dakika kala)
        if (accessToken && this.isTokenExpiringSoon(accessToken)) {
            await this.refreshTokenIfNeeded()
        }

        // AbortController ile timeout desteği
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), timeout)

        try {
            // İlk istek
            let response = await fetch(url, {
                ...options,
                signal: controller.signal,
                headers: {
                    'Content-Type': 'application/json',
                    ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
                    ...options.headers,
                },
            })

            clearTimeout(timeoutId)

            // 401 hatası alırsak token'ı yenilemeyi dene
            if (response.status === 401 && refreshToken) {
                try {
                    const refreshResponse = await fetch(`${this.baseURL}/api/auth/refresh`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ refreshToken }),
                    })

                    if (refreshResponse.ok) {
                        const { accessToken: newAccessToken, refreshToken: newRefreshToken } = await refreshResponse.json()

                        // Yeni token'ları store'a kaydet
                        useAuthStore.getState().refreshAccessToken(newAccessToken)
                        useAuthStore.setState({ refreshToken: newRefreshToken })

                        // Orijinal isteği yeni token ile tekrar dene
                        response = await fetch(url, {
                            ...options,
                            signal: controller.signal,
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${newAccessToken}`,
                                ...options.headers,
                            },
                        })
                        clearTimeout(timeoutId)
                    } else {
                        clearTimeout(timeoutId)
                        // Refresh başarısız, logout yap
                        logout()
                        throw new Error('Oturum süresi doldu. Lütfen tekrar giriş yapın.')
                    }
                } catch (error) {
                    clearTimeout(timeoutId)
                    if (error instanceof Error && error.name === 'AbortError') {
                        throw new Error('İstek zaman aşımına uğradı. Lütfen tekrar deneyin.')
                    }
                    logout()
                    throw new Error('Oturum süresi doldu. Lütfen tekrar giriş yapın.')
                }
            }

            return response
        } catch (error) {
            clearTimeout(timeoutId)
            if (error instanceof Error && error.name === 'AbortError') {
                throw new Error('İstek zaman aşımına uğradı. Lütfen tekrar deneyin.')
            }
            throw error
        }
    }

    private isTokenExpiringSoon(token: string): boolean {
        try {
            const payload = JSON.parse(atob(token.split('.')[1]))
            const exp = payload.exp * 1000
            const now = Date.now()
            const fiveMinutes = 5 * 60 * 1000

            return (exp - now) < fiveMinutes
        } catch {
            return true
        }
    }

    private async refreshTokenIfNeeded(): Promise<void> {
        const { refreshToken, logout } = useAuthStore.getState()

        if (!refreshToken) {
            logout()
            return
        }

        try {
            const response = await fetch(`${this.baseURL}/api/auth/refresh`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ refreshToken }),
            })

            if (response.ok) {
                const { accessToken: newAccessToken, refreshToken: newRefreshToken } = await response.json()
                useAuthStore.getState().refreshAccessToken(newAccessToken)
                useAuthStore.setState({ refreshToken: newRefreshToken })
            } else {
                logout()
            }
        } catch (error) {
            logout()
        }
    }

    async get(url: string, options: RequestInit = {}): Promise<Response> {
        return this.makeRequest(`${this.baseURL}${url}`, {
            ...options,
            method: 'GET',
        })
    }

    async post(url: string, data?: any, options: RequestInit = {}, timeout: number = 30000): Promise<Response> {
        return this.makeRequest(`${this.baseURL}${url}`, {
            ...options,
            method: 'POST',
            body: data ? JSON.stringify(data) : undefined,
        }, timeout)
    }

    async put(url: string, data?: any, options: RequestInit = {}): Promise<Response> {
        return this.makeRequest(`${this.baseURL}${url}`, {
            ...options,
            method: 'PUT',
            body: data ? JSON.stringify(data) : undefined,
        })
    }

    async delete(url: string, options: RequestInit = {}): Promise<Response> {
        return this.makeRequest(`${this.baseURL}${url}`, {
            ...options,
            method: 'DELETE',
        })
    }
}

export const apiClient = new ApiClient()




